// Picks indicators from the catalog and edits their per-instance parameters.
// Visual layout matches bb-traderx's strategy wizard:
//   • Responsive sparkline-card grid (cols-2 / sm:3 / lg:4 / xl:5)
//   • Selected indicators stack their instance cards DIRECTLY BELOW their picker card
//   • Each instance card is a thinner purple-bordered surface with inline params
//   • Multi-instance counts surface via a "2×" badge on the picker card
//
// The 25 indicators are grouped (Oscillators / Trends / Channels / Volatility) so the
// user can scan visually by indicator family.

import { useMemo, type ReactNode } from "react";
import { X } from "lucide-react";
import { useBuilderState } from "../../state/BuilderState";
import { useIndicatorCatalog } from "../../hooks/useIndicatorCatalog";
import { IndicatorCard } from "./IndicatorCard";
import { IndicatorParamEditor } from "./IndicatorParamEditor";
import { SectionHeader } from "../shell/SectionHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { IndicatorSpecDto } from "../../api/types";

const GROUP_ORDER = ["Oscillators", "Trends", "Channels", "Volatility"];

export function IndicatorPicker({ headerSlot }: { headerSlot?: ReactNode } = {}) {
  const { draft, patch } = useBuilderState();
  const { catalog, loading, error } = useIndicatorCatalog();

  const byBaseId = useMemo(() => {
    const m = new Map<string, (typeof catalog)[number]>();
    for (const c of catalog) m.set(c.baseId, c);
    return m;
  }, [catalog]);

  const instancesByBaseId = useMemo(() => {
    const m = new Map<string, IndicatorSpecDto[]>();
    for (const s of draft.indicatorSpecs) {
      const list = m.get(s.baseId) ?? [];
      list.push(s);
      m.set(s.baseId, list);
    }
    return m;
  }, [draft.indicatorSpecs]);

  const addInstance = (baseId: string) => {
    const entry = byBaseId.get(baseId);
    if (!entry) return;
    const usedIndices = new Set<number>(
      draft.indicatorSpecs
        .filter((s) => s.baseId === baseId)
        .map((s) => {
          const m = s.instanceKey.match(/_(\d+)$/);
          return m ? parseInt(m[1]!, 10) : 0;
        }),
    );
    let n = 0;
    while (usedIndices.has(n)) n++;
    const defaults: Record<string, number> = {};
    for (const d of entry.paramDescriptors) defaults[d.key] = d.default;
    const newSpec: IndicatorSpecDto = {
      instanceKey: `${baseId}_${n}`,
      baseId,
      params: defaults,
    };
    patch({ indicatorSpecs: [...draft.indicatorSpecs, newSpec] });
  };

  const removeInstance = (instanceKey: string) => {
    patch({ indicatorSpecs: draft.indicatorSpecs.filter((s) => s.instanceKey !== instanceKey) });
  };

  const updateParam = (instanceKey: string, key: string, value: number) => {
    patch({
      indicatorSpecs: draft.indicatorSpecs.map((s) =>
        s.instanceKey === instanceKey ? { ...s, params: { ...s.params, [key]: value } } : s,
      ),
    });
  };

  const groups = useMemo(() => groupByGroup(catalog), [catalog]);
  const orderedGroupNames = useMemo(() => {
    const present = Object.keys(groups);
    return [
      ...GROUP_ORDER.filter((g) => present.includes(g)),
      ...present.filter((g) => !GROUP_ORDER.includes(g)),
    ];
  }, [groups]);

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <SectionHeader
            title="Indicators"
            subtitle="Click a card to add it. Add the same indicator multiple times with different parameters to express crossover logic (e.g. EMA-20 vs EMA-50)."
            badge={`${draft.indicatorSpecs.length} active`}
          />
        </div>
        {headerSlot}
      </div>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-[110px] rounded-xl" />
          ))}
        </div>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Couldn't load indicator catalog: {error}</AlertDescription>
        </Alert>
      )}
      {!loading && !error &&
        orderedGroupNames.map((group) => (
          <div key={group} className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-bold">
              {group}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {groups[group]!.map((entry) => {
                const instances = instancesByBaseId.get(entry.baseId) ?? [];
                return (
                  <div key={entry.baseId} className="flex flex-col gap-1.5">
                    <IndicatorCard
                      indicator={entry}
                      instanceCount={instances.length}
                      onAdd={() => addInstance(entry.baseId)}
                    />

                    {/* Instance cards stacked below — match bb-traderx's pattern. */}
                    {instances.map((spec, idx) => (
                      <div
                        key={spec.instanceKey}
                        className={cn(
                          "flex flex-col gap-1.5 p-2.5 rounded-xl border border-primary/40 bg-primary/5",
                        )}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[10px] font-semibold text-primary">
                            {entry.displayName}
                            {instances.length > 1 && ` #${idx + 1}`}
                          </span>
                          <button
                            onClick={() => removeInstance(spec.instanceKey)}
                            className="size-4 flex items-center justify-center rounded-full text-muted-foreground/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            aria-label={`Remove ${spec.instanceKey}`}
                          >
                            <X className="size-2.5" />
                          </button>
                        </div>
                        <IndicatorParamEditor
                          descriptors={entry.paramDescriptors}
                          values={spec.params}
                          onChange={(k, v) => updateParam(spec.instanceKey, k, v)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </section>
  );
}

function groupByGroup<T extends { group: string }>(items: T[]): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of items) {
    (result[item.group] ??= []).push(item);
  }
  return result;
}
